import React, { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { bffAuthService } from '../../services/BffAuthService';

import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';

import {
  IconButton,
  Menu,
  Avatar,
  MenuItem,
  Drawer,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListItem,
  Divider,
  Typography,
  List,
  Toolbar,
  Box
} from '@mui/material';

import { Menu as MenuIcon, Settings as SettingsIcon } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { useAppManager } from '../../hooks/managers/useAppManager';
import { useUserManager } from '../../hooks/managers/useUserManager';
import { hostServerAddress, mainHeaderHeight, drawerWidth, drawerHeaderMinHeight, toolBarPaddingLeftRight } from '../../common/MetaSetting';
import { ComponentStyles } from '../../common/ThemeColors';

import { LIB_MENU_LIST, MENU_LIST, SETTINGS_MENU_LIST, SYSTEM_MENU_LIST, ROUTES } from '../constants';

import { Outlet } from 'react-router-dom';
import { LayoutContent } from './LayoutContent';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { MessageType, MessageShape } from '../../model/AppUIShapes';
import { useSnackbar } from 'notistack';
import { UserAvatar } from '../components/UserAvatar';

const leftDrawerWidth = drawerWidth;
const headerHeight = mainHeaderHeight;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar
}));

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })
}));

let messageAppendTimer: any;
let legacyMessages: MessageShape[] = [];

export const MLayout = () => {
  const { t } = useTranslation();

  const { removeMessageFromLocal, messages } = useAppManager();
  const { currentUser } = useUserManager();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    triggerNoticficationByMessages(messages);
  }, [messages]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // 用户信息项主体：与 memo-flutter ReliableNavigation 的用户行一致，点击进入设置页
  const handleUserItemClick = () => {
    navigate(ROUTES.settings);
    handleDrawerClose();
  };

  const handleLogout = () => {
    bffAuthService.logout().then(() => window.location.href = '/login?logged_out=true');
  };

  const handleNoticationsClose = (messageId: string) => {
    removeMessageFromLocal(messageId);
  };

  const triggerNoticficationByMessages = (messageList: MessageShape[]) => {
    messageList.forEach((msg) => {
      let foundMessages = legacyMessages.filter((lm) => lm.guid === msg.guid);
      if (foundMessages.length < 1) {
        legacyMessages = [...legacyMessages, msg];
        clearTimeout(messageAppendTimer);
        messageAppendTimer = setTimeout(() => (legacyMessages = []), 30000);
        enqueueSnackbar(t(msg.message), {
          variant: convertMessageType(msg),
          autoHideDuration: 6000,
          onClose: () => handleNoticationsClose(msg.guid),
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
      }
    });
  };

  const convertMessageType = (message: MessageShape) => {
    switch (message.messageType) {
      case MessageType.SUCCESS:
        return 'success';
      case MessageType.INFO:
        return 'info';
      case MessageType.WARNING:
        return 'warning';
      case MessageType.ERROR:
        return 'error';
      default:
        return 'default';
    }
  };

  const { pathname } = useLocation();

  let mainTitle = '';
  MENU_LIST.map((mi, index) => {
    if (pathname === mi.route) mainTitle = t(mi.literal);
  });
  SYSTEM_MENU_LIST.map((mi, index) => {
    if (pathname === mi.route) mainTitle = t(mi.literal);
  });
  LIB_MENU_LIST.map((mi, index) => {
    if (pathname === mi.route) mainTitle = t(mi.literal);
  });
  SETTINGS_MENU_LIST.map((mi, index) => {
    if (pathname === mi.route) mainTitle = t(mi.literal);
  });

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar 配色对齐 memo-flutter：背景 appBarBackground（浅白/深 #1A1A1A），前景品牌色 */}
      <AppBar position="fixed" sx={{ backgroundColor: 'background.paper', color: 'primary.main' }}>
        <Toolbar variant="dense" sx={{ margin: 0, paddingLeft: toolBarPaddingLeftRight as any, paddingRight: toolBarPaddingLeftRight as any }}>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                flexGrow: 0,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'start',
                alignItems: 'center'
              }}
            >
              <IconButton
                color="inherit"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  color: 'inherit',
                }}
              >
                <MenuIcon sx={{ color: 'primary.main' }} />
              </IconButton>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'start',
                  alignItems: 'center',
                  color: 'primary.main'
                }}
              >
                {mainTitle}
              </Typography>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            ></Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={open}
        onClose={handleDrawerClose}
        sx={{ '& .MuiDrawer-paper': { width: leftDrawerWidth, boxSizing: 'border-box' } }}
      >
        <DrawerHeader
          sx={{
            // 对齐 memo-flutter 导航头部：surfaceContainerHighest @ 50%
            backgroundColor: theme.palette.mode === 'dark' ? '#28272B' : '#F2F0F4',
            color: 'primary.main',
            minHeight: drawerHeaderMinHeight as any
          }}
        >
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              height: `${headerHeight}`,
              padding: '5px',
              borderRadius: ComponentStyles.base.borderRadius.default,
              bgcolor: 'background.paper'
            }}
          >
            <Avatar src="/logo192.png" />
            <Box>
              <span style={{ color: theme.palette.primary.main }}>{t('wordPrefixWise')}</span>
              <span style={{ color: theme.palette.text.secondary }}>{t('wordLeftWise')}</span>
              <span style={{ color: theme.palette.primary.main }}>{t('wordPrefixMemo')}</span>
              <span style={{ color: theme.palette.text.secondary }}>{t('wordLeftMemo')}</span>
            </Box>
          </Box>
        </DrawerHeader>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            width: '100%',
            height: `calc(100vh - ${mainHeaderHeight})`,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List dense sx={{ py: 1 }}>
              {MENU_LIST.map((mi, index) => (
                <Link key={mi.literal} to={mi.route}>
                  <ListItem
                    key={mi.literal}
                    disablePadding
                    selected={pathname === mi.route}
                    sx={{
                      display: 'block',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        color: 'primary.main'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'primary.main'
                      }
                    }}
                    onClick={handleDrawerClose}
                    dense
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        color: 'inherit',
                        pl: 2,
                        pr: open ? 2 : 1
                      }}
                      dense
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: open ? 0 : 'auto',
                          mr: open ? 1 : 0,
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                          color: 'inherit'
                        }}
                      >
                        <mi.Icon />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontWeight: 'normal', fontSize: '1em' }} primary={t(mi.literal)} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>
                </Link>
              ))}
            </List>
            <Divider />
            <List dense sx={{ py: 1 }}>
              {SYSTEM_MENU_LIST.map((mi, index) => (
                <Link key={mi.literal} to={mi.route}>
                  <ListItem
                    key={mi.literal}
                    disablePadding
                    selected={pathname === mi.route}
                    sx={{
                      display: 'block',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        color: 'primary.main'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'primary.main'
                      }
                    }}
                    onClick={handleDrawerClose}
                    dense
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        color: 'inherit',
                        pl: 2,
                        pr: open ? 2 : 1
                      }}
                      dense
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: open ? 0 : 'auto',
                          mr: open ? 1 : 0,
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                          color: 'inherit'
                        }}
                      >
                        <mi.Icon />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontWeight: 'normal', fontSize: '1em' }} primary={t(mi.literal)} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>
                </Link>
              ))}
            </List>
            {/* <Divider />
            <List dense>
              {LIB_MENU_LIST.map((mi, index) => (
                <Link key={mi.literal} to={mi.route}>
                  <ListItem
                    key={mi.literal}
                    disablePadding
                    selected={pathname === mi.route}
                    sx={{
                      display: 'block',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        color: 'primary.main'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'primary.main'
                      }
                    }}
                    onClick={handleDrawerClose}
                    dense
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: 'initial',
                        color: 'inherit'
                      }}
                      dense
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : 'auto',
                          justifyContent: 'center',
                          color: 'inherit'
                        }}
                      >
                        <mi.Icon />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontWeight: 'normal', fontSize: '1em' }} primary={t(mi.literal)} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </ListItem>
                </Link>
              ))}
            </List> */}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'stretch',
              flexShrink: 0
            }}
          >
            <Divider />
            <List dense sx={{ py: 1 }}>
              {/* 用户信息项：对齐 memo-flutter ReliableNavigation 的用户行设计
                  （头像 + 昵称 + @用户名 + 尾部设置图标），点击进入设置页；
                  尾部设置图标打开 个人资料/退出 菜单（Web 端保留唯一登出入口） */}
              <ListItem
                disablePadding
                selected={pathname === ROUTES.settings}
                sx={{
                  display: 'block',
                  color: 'text.primary',
                  borderRadius: 1,
                  mx: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.main'
                  },
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.main'
                  }
                }}
                dense
              >
                <ListItemButton
                  onClick={handleUserItemClick}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    color: 'inherit',
                    pl: 2,
                    pr: open ? 1 : 0.5
                  }}
                  dense
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? 0 : 'auto',
                      mr: open ? 1 : 0,
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                      color: 'inherit'
                    }}
                  >
                    <UserAvatar
                      nickName={currentUser?.nickName}
                      name={currentUser?.name}
                      username={currentUser?.username}
                      avatarPath={currentUser?.avatarPath}
                      size={32}
                    />
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={
                        <Typography
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: pathname === ROUTES.settings ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {currentUser?.nickName || currentUser?.name || currentUser?.username || t('menuProfile', '个人资料')}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: '0.75rem',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: pathname === ROUTES.settings ? 'primary.main' : 'text.secondary'
                          }}
                        >
                          {currentUser?.username ? `@${currentUser.username}` : currentUser?.userId || ''}
                        </Typography>
                      }
                    />
                  )}
                  {open && (
                    <IconButton
                      size="small"
                      aria-label={t('menuSettings')}
                      onClick={handleOpenUserMenu}
                      sx={{
                        ml: 'auto',
                        color: pathname === ROUTES.settings ? 'primary.main' : 'text.secondary'
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
              <Menu
                id="user-menu-sidebar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem dense key="profileItem" onClick={() => { handleCloseUserMenu(); window.open('https://user.wisememo.com', '_blank'); }}>
                  <Typography textAlign="center" component="span">
                    {t('buttonProfile')}
                  </Typography>
                </MenuItem>
                <MenuItem dense key="logoutItem" onClick={handleLogout}>
                  <Typography textAlign="center" component="span">
                    {t('buttonLogout')}
                  </Typography>
                </MenuItem>
              </Menu>
            </List>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
                px: 1.5,
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              {/* 与 memo-flutter 侧栏底部版权信息一致（navCopyright） */}
              <Typography variant="caption" color="text.secondary" sx={{ opacity: open ? 1 : 0, textAlign: 'center' }}>
                {t('navCopyright', '© 2026 极智笔记 | 版权所有')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <LayoutContent>
        {/* 路由级错误边界：子页面渲染异常时保留导航壳，不白屏（05-5.13） */}
        <ErrorBoundary variant="page">
          <Outlet />
        </ErrorBoundary>
      </LayoutContent>
    </Box>
  );
};
